import {
  AutoscrollToBottom,
  ListScrollLocation,
  VirtuosoMessageListMethods,
  VirtuosoMessageListProps,
} from "@virtuoso.dev/message-list";
import {
  MessageFragment,
  MessageListInput,
  MessagesQuery,
  MessagesQueryVariables,
  MessageThreadFragment,
  MessageThreadType,
  PagedMessageListQuery,
  PagedMessageListQueryVariables,
} from "gql/graphql";
import MESSAGES_QUERY from "modules/messaging/_networking/messages";
import PAGED_MESSAGE_LIST_QUERY from "modules/messaging/_networking/paged-message-list";
import { useAuth } from "modules/providers/auth-provider";
import { usePubSub } from "modules/providers/pub-sub-provider";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isSameMessage, sortByCreateTime } from "modules/utils/Functions";
import useBurstQuery from "./use-burst-query";
import useRemovedFromThread from "./use-removed-from-thread";
import PQueue from "p-queue";
import { EVENT_TYPE } from "modules/providers/pub-sub-provider/pub-sub-provider";
import { useRouter } from "next/router";
import useQueryString, {
  MESSAGE_ID_QUERY_STRING,
  MESSAGE_SEARCH_QUERY_STRING,
  TIMESTAMP_QUERY_STRING,
} from "./use-query-string";
import { IMessageFragmentWithIsDraft } from "modules/messaging/message-thread/message-thread-message";
import debounce from "lodash/debounce";
import VirtuosoMessage from "modules/messaging/message-thread/virtuoso-message";

export type ScrollDirection = "up" | "down";

export interface VirtuosoMessageListContext {
  messageThreadId: string;
  isMeRemoved: boolean;
  isPrepending: boolean;
  isAppending: boolean;
  type: MessageThreadType;
  hasLastMessage: boolean;
  hasFirstMessage: boolean;
  initializeMessageList: (startAtTop: boolean) => void;
}

export interface VirtuosoMessageListData {
  data: IMessageFragmentWithIsDraft;
  index: number;
  context: VirtuosoMessageListContext;
}

interface IProps {
  messageThread: MessageThreadFragment;
  startAtTop: boolean;
  setScrollDirection?: (scrollDirection: ScrollDirection) => void;
}

const INITIAL_PAGE_SIZE = 15;
const AT_TOP_THRESHOLD = -100;
// pixels of already-read context kept visible above the unread line when a
// thread opens at its first unseen message
const UNREAD_LINE_TOP_OFFSET = -100;
// how long after an initial load the target location keeps being re-asserted
// while items settle; released early on any user scroll intent
const INITIAL_LOCATION_HOLD_MS = 2500;

const usePagedMessageList = ({
  messageThread,
  startAtTop,
  setScrollDirection,
}: IProps) => {
  const { me } = useAuth();
  const { qsTimestamp, qsMessageId } = useQueryString();
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const queueRef = useRef(new PQueue({ concurrency: 1 }));
  const virtuosoRef =
    useRef<
      VirtuosoMessageListMethods<MessageFragment, VirtuosoMessageListContext>
    >(null);

  const messageThreadRef = useRef(messageThread);
  messageThreadRef.current = messageThread;

  const { isMeRemoved } = useRemovedFromThread(messageThread);

  const [pagedMessageListAPI] = useBurstQuery<
    PagedMessageListQuery,
    PagedMessageListQueryVariables
  >(PAGED_MESSAGE_LIST_QUERY);

  const [messagesAPI] = useBurstQuery<MessagesQuery, MessagesQueryVariables>(
    MESSAGES_QUERY,
  );

  const [unseenMessageId, setUnseenMessageId] = useState<string>();
  const [hasLastMessage, setHasLastMessage] = useState(false);

  const getMessageList = useCallback(() => {
    if (!virtuosoRef.current) return [];
    return virtuosoRef.current.data.get();
  }, [virtuosoRef]);

  const { subscribe, unsubscribe } = usePubSub();

  const _setScrollDirection = useCallback(
    (direction: ScrollDirection) => {
      if (setScrollDirection) {
        setScrollDirection(direction);
      }
    },
    [setScrollDirection],
  );

  const _onNewMessageScrollToBottom = useCallback(
    (
      message: MessageFragment,
      scrollLocation: ListScrollLocation,
    ): ScrollBehavior | boolean => {
      if (!me) return false;
      if (message.createdUserId === me.id) {
        return "instant";
      }
      const nearBottom =
        scrollLocation.bottomOffset / scrollLocation.scrollHeight < 0.05;
      if (nearBottom) {
        return "smooth";
      }
      return false;
    },
    [me],
  );

  const onDraftMessageSent = useCallback(
    async (message: IMessageFragmentWithIsDraft) => {
      if (!me) return;
      if (!virtuosoRef.current) return;
      if (message.messageThreadId !== messageThreadRef.current.id) return;

      const onNewMessageScrollToBottom: AutoscrollToBottom<
        MessageFragment,
        VirtuosoMessageListContext
      > = ({ scrollLocation }) =>
        _onNewMessageScrollToBottom(message, scrollLocation);

      // fetch the end of the message list so the message can be appended to the list
      if (!hasLastMessage && message.createdUserId === me.id) {
        let response;
        try {
          response = await pagedMessageListAPI({
            variables: {
              input: {
                messageThreadId: messageThreadRef.current.id,
                pageSize: INITIAL_PAGE_SIZE,
                messageCreateTime: moment().toISOString(),
              },
            },
            fetchPolicy: "no-cache",
          });
        } catch {
          return;
        }
        if (!virtuosoRef.current) return;
        const _messageList = response.data?.pagedMessageList ?? [];

        const appendMessage = !_messageList.some((_message) =>
          isSameMessage(_message, message),
        );
        if (appendMessage) {
          _messageList.push(message);
          _messageList.sort(sortByCreateTime);
        }
        virtuosoRef.current.data.replace(_messageList, {
          initialLocation: {
            index: "LAST",
            align: "end",
            behavior: "auto",
          },
          purgeItemSizes: true,
        });
        const lastMessage = messageThreadRef.current.lastMessage;
        setHasLastMessage(
          _messageList.some((message) => isSameMessage(message, lastMessage)),
        );
        return;
      }

      virtuosoRef.current.data.append([message], onNewMessageScrollToBottom);
    },
    [
      _onNewMessageScrollToBottom,
      hasLastMessage,
      me,
      pagedMessageListAPI,
      virtuosoRef,
    ],
  );

  const onMessage = useCallback(
    async (eventType: EVENT_TYPE, message: IMessageFragmentWithIsDraft) => {
      if (!me) return;
      if (!virtuosoRef.current) return;
      if (message?.messageThreadId !== messageThreadRef.current.id) return;

      // message has been updated; replace the message in the list
      let isUpdated = false;
      virtuosoRef.current.data.map((_message) => {
        if (isSameMessage(_message, message)) {
          isUpdated = true;
          return message;
        }
        return _message;
      });

      if (isUpdated) {
        return;
      }

      //Scroll function
      const onNewMessageScrollToBottom: AutoscrollToBottom<
        MessageFragment,
        VirtuosoMessageListContext
      > = ({ scrollLocation }) =>
        _onNewMessageScrollToBottom(message, scrollLocation);

      if (hasLastMessage && eventType === "new-message") {
        virtuosoRef.current.data.append([message], onNewMessageScrollToBottom);
      }
    },
    [_onNewMessageScrollToBottom, hasLastMessage, me, virtuosoRef],
  );

  const onMessageSeen = useCallback(
    (messageId: string) => {
      if (!virtuosoRef.current) return;
      virtuosoRef.current.data.map((message) => {
        if (message.id === messageId) {
          return {
            ...message,
            isSeen: true,
          };
        }
        return message;
      });
    },
    [virtuosoRef],
  );

  const addDraftMessageToMessageList = useCallback(
    (message: MessageFragment) => {
      queueRef.current.add(() =>
        onDraftMessageSent({
          ...message,
          isDraft: true,
        }),
      );
    },
    [onDraftMessageSent],
  );

  const addNewMessageToQueue = useCallback(
    (message: MessageFragment) => {
      queueRef.current.add(() => onMessage("new-message", message));
    },
    [onMessage],
  );

  const addUpdatedMessageToQueue = useCallback(
    (message: MessageFragment) => {
      queueRef.current.add(() => onMessage("message-updated", message));
    },
    [onMessage],
  );

  const refetchMessageList = useCallback(
    async (messageThreadId: string) => {
      if (messageThreadId !== messageThreadRef.current.id) return;
      if (!virtuosoRef.current) return;
      let response;
      try {
        response = await messagesAPI({
          variables: {
            id: getMessageList().map((message) => message.id),
          },
        });
      } catch {
        return;
      }
      const _messageList = response.data?.messages;
      if (!_messageList) return;
      // plain replace on purpose: same items with updated fields, and the
      // scroll position must be preserved — never pass purgeItemSizes or
      // initialLocation here
      virtuosoRef.current?.data.replace(_messageList);
    },
    [getMessageList, messagesAPI, virtuosoRef],
  );

  const initialLocationHoldCleanupRef = useRef<(() => void) | null>(null);

  // virtuoso's anchored initial location only holds until the first scroll
  // completes; on slow devices/networks items keep re-measuring after that,
  // displacing the landing position (the unread line ends up off-screen even
  // though it rendered in the right place). Keep re-asserting the target for
  // a short window whenever the list content resizes, releasing on the first
  // sign of user scroll intent or after the hold window elapses.
  const holdInitialLocation = useCallback(
    (target: {
      messageId?: string;
      index: number | "LAST";
      align: "start" | "center" | "end" | "start-no-overflow";
      offset?: number;
    }) => {
      initialLocationHoldCleanupRef.current?.();
      const scroller = virtuosoRef.current?.scrollerElement();
      if (!scroller) return;
      const content = scroller.firstElementChild ?? scroller;

      const reapply = () => {
        if (!virtuosoRef.current) return;
        let index = target.index;
        if (target.messageId) {
          // resolve by id in case a prepend shifted the indices during the hold
          const found = virtuosoRef.current.data.findIndex(
            (message) => message.id === target.messageId,
          );
          if (found > -1) index = found;
        }
        virtuosoRef.current.scrollToItem({
          index,
          align: target.align,
          behavior: "auto",
          offset: target.offset,
        });
      };

      let stopped = false;
      const stop = () => {
        if (stopped) return;
        stopped = true;
        observer.disconnect();
        clearTimeout(timer);
        scroller.removeEventListener("wheel", stop);
        scroller.removeEventListener("touchstart", stop);
        scroller.removeEventListener("keydown", stop);
        scroller.removeEventListener("pointerdown", stop);
        if (initialLocationHoldCleanupRef.current === stop) {
          initialLocationHoldCleanupRef.current = null;
        }
      };

      const observer = new ResizeObserver(() => reapply());
      observer.observe(content);
      scroller.addEventListener("wheel", stop, { passive: true });
      scroller.addEventListener("touchstart", stop, { passive: true });
      scroller.addEventListener("keydown", stop);
      scroller.addEventListener("pointerdown", stop);
      const timer = setTimeout(stop, INITIAL_LOCATION_HOLD_MS);
      initialLocationHoldCleanupRef.current = stop;
    },
    [],
  );

  useEffect(
    () => () => {
      initialLocationHoldCleanupRef.current?.();
    },
    [],
  );

  const loadInitialMessagePage = useCallback(
    async (_startAtTop: boolean, honorMessageId = true) => {
      if (!virtuosoRef.current) return;

      const _messageId = honorMessageId ? qsMessageId : undefined;

      const input: MessageListInput = {
        messageThreadId: messageThreadRef.current.id,
        pageSize: INITIAL_PAGE_SIZE,
      };

      if (_messageId) {
        input.messageId = _messageId;
      } else if (_startAtTop) {
        input.messageCreateTime = new Date(0).toISOString();
        input.isAfter = true;
      }

      const response = await pagedMessageListAPI({
        variables: {
          input,
        },
        //caching this query produces a bug where the message list is missing new messages
        fetchPolicy: "no-cache",
      });
      let _messageList = response.data?.pagedMessageList;
      if (!_messageList) return;
      _messageList.sort(sortByCreateTime);

      const lastMessage = messageThreadRef.current.lastMessage;
      let _hasLastMessage = _messageList.some((message) =>
        isSameMessage(message, lastMessage),
      );

      // the backend anchors the default page at the user's
      // lastSeenMessageCreateTime; that pointer can lag reality (it is not
      // advanced by other devices, so messages read elsewhere leave it stale),
      // which yields a mid-thread window with nothing unseen and no last
      // message — nothing to anchor to. Re-fetch the true end of the thread
      // instead of stranding the user in the middle.
      if (
        !_messageId &&
        !_startAtTop &&
        !_hasLastMessage &&
        !_messageList.some((message) => !message.isSeen)
      ) {
        const fallbackResponse = await pagedMessageListAPI({
          variables: {
            input: {
              messageThreadId: messageThreadRef.current.id,
              pageSize: INITIAL_PAGE_SIZE,
              messageCreateTime: moment().toISOString(),
              isAfter: false,
            },
          },
          fetchPolicy: "no-cache",
        });
        _messageList = fallbackResponse.data?.pagedMessageList ?? _messageList;
        _messageList.sort(sortByCreateTime);
        _hasLastMessage = _messageList.some((message) =>
          isSameMessage(message, lastMessage),
        );
      }

      setHasLastMessage(_hasLastMessage);
      setHasMoreAfter(!_hasLastMessage);

      // the unread line sits above the oldest unseen message in the page;
      // findIndex also covers the cases the old boundary scan missed (only the
      // last message unseen, the whole page unseen, or a seen own-message after
      // unseen ones)
      let _unseenMessageIndex = _messageList.findIndex(
        (message) => !message.isSeen,
      );

      // a page that is unseen from its very first message AND contains the
      // thread's last message means the user has never seen anything in this
      // thread (first visit, or they were just added): the backend returns
      // the end of the thread and there is no read/unread boundary to mark.
      // Entering a brand-new thread should not show an unread line — land at
      // the bottom like a normal entry. (A genuine backlog page anchors at
      // the user's last-seen position and includes read context above the
      // first unseen message, so the line still shows for those.)
      const isThreadNewToUser = _unseenMessageIndex === 0 && _hasLastMessage;
      if (isThreadNewToUser) {
        _unseenMessageIndex = -1;
      }

      if (_unseenMessageIndex > -1) {
        setUnseenMessageId(_messageList[_unseenMessageIndex].id);
      }

      let scrollIndex: number | "LAST" = "LAST";
      let align: "start" | "center" | "end" | "start-no-overflow" = "end";
      let offset = 0;

      if (_messageId) {
        const qsIndex = _messageList.findIndex((m) => m.id === _messageId);
        scrollIndex = qsIndex > -1 ? qsIndex : "LAST";
        align = qsIndex > -1 ? "center" : "end";
      } else if (_startAtTop) {
        scrollIndex = 0;
        align = "start";
      } else if (_unseenMessageIndex > -1) {
        // anchor the unread line near the top of the viewport (with a little
        // read context above it) so the unread messages read downward.
        // "start-no-overflow" (rather than "start") stops the position from
        // scrolling past the end of the content when the unread run is
        // shorter than the viewport — plain "start" leaves a blank gap below
        // the last message in that case
        scrollIndex = _unseenMessageIndex;
        align = "start-no-overflow";
        offset = UNREAD_LINE_TOP_OFFSET;
      }

      // the awaits above can outlive the list (thread switch/unmount)
      if (!virtuosoRef.current) return;

      // purgeItemSizes routes initialLocation through the library's anchored
      // path: the render window is derived from the target location and the
      // scroll is re-applied as items resize, so late-growing content
      // (email HTML, quill) can't leave the list at a stale offset. It also
      // drops cached item sizes left over from the previous thread, since this
      // component is reused across threads without remounting.
      virtuosoRef.current.data.replace(_messageList, {
        initialLocation: {
          index: scrollIndex,
          align,
          behavior: "auto",
          offset,
        },
        purgeItemSizes: true,
      });

      holdInitialLocation({
        messageId:
          typeof scrollIndex === "number"
            ? _messageList[scrollIndex]?.id
            : undefined,
        index: scrollIndex,
        align,
        offset,
      });
    },
    [holdInitialLocation, pagedMessageListAPI, qsMessageId],
  );

  const initializeMessageList = useCallback(
    (overrideStartAtTop?: boolean) => {
      // an explicit boolean means the user deliberately jumped to an edge
      // (scroll-to-top/bottom button); a deep-linked message in the URL must
      // not win over that, so ignore it for this load. Only remove it from the
      // URL when no in-thread search is active — during a search the message
      // param is the search cursor (the header's "N of M" position and
      // prev/next both derive from it), so it must survive the jump.
      const isExplicitJump = typeof overrideStartAtTop === "boolean";
      const isSearchActive =
        routerRef.current.query[MESSAGE_SEARCH_QUERY_STRING] === "true";
      if (isExplicitJump && !isSearchActive) {
        const query = { ...routerRef.current.query };
        if (
          MESSAGE_ID_QUERY_STRING in query ||
          TIMESTAMP_QUERY_STRING in query
        ) {
          delete query[MESSAGE_ID_QUERY_STRING];
          delete query[TIMESTAMP_QUERY_STRING];
          routerRef.current.replace({ query });
        }
      }
      setUnseenMessageId(undefined);
      loadInitialMessagePage(
        overrideStartAtTop ?? startAtTop,
        !isExplicitJump,
      ).catch(() => {
        // a failed initial page fetch leaves the list empty; the next
        // navigation or ws-reconnect refetch recovers it
      });
      setHasMoreBefore(true);
      setHasLastMessage(false);
      setHasMoreAfter(true);
      _setScrollDirection("up");
    },
    [_setScrollDirection, loadInitialMessagePage, startAtTop],
  );

  // one entry per navigation signal; re-running with an unchanged signature
  // (e.g. StrictMode double-invoke) is a no-op
  const lastInitSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const signature = `${messageThread.id}|${qsMessageId ?? ""}|${qsTimestamp ?? ""}`;
    if (lastInitSignatureRef.current === signature) return;
    const isNewThread = !lastInitSignatureRef.current?.startsWith(
      `${messageThread.id}|`,
    );
    lastInitSignatureRef.current = signature;

    if (isNewThread) {
      initializeMessageList();
      return;
    }

    if (!qsMessageId) return;
    if (!virtuosoRef.current) return;

    const index = virtuosoRef.current.data.findIndex(
      (m) => m.id === qsMessageId,
    );

    if (index > -1) {
      // the target is already rendered and the list has settled, so a smooth
      // scroll is safe here
      virtuosoRef.current.scrollToItem({
        index,
        align: "center",
        behavior: "smooth",
      });
    } else {
      initializeMessageList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageThread.id, qsMessageId, qsTimestamp]);

  useEffect(() => {
    subscribe("draft-message-sent", addDraftMessageToMessageList);
    subscribe("message-seen", onMessageSeen);
    subscribe("new-message", addNewMessageToQueue);
    subscribe("message-updated", addUpdatedMessageToQueue);
    subscribe("message-thread-translated", refetchMessageList);
    subscribe("ws-reconnect", initializeMessageList);
    return () => {
      unsubscribe("draft-message-sent", addDraftMessageToMessageList);
      unsubscribe("message-seen", onMessageSeen);
      unsubscribe("new-message", addNewMessageToQueue);
      unsubscribe("message-updated", addUpdatedMessageToQueue);
      unsubscribe("message-thread-translated", refetchMessageList);
      unsubscribe("ws-reconnect", initializeMessageList);
    };
  }, [
    addDraftMessageToMessageList,
    addNewMessageToQueue,
    onMessageSeen,
    addUpdatedMessageToQueue,
    initializeMessageList,
    refetchMessageList,
    subscribe,
    unsubscribe,
  ]);

  const isPrependingRef = useRef(false);
  const isAppendingRef = useRef(false);
  const [isPrepending, setIsPrepending] = useState(false);
  const [isAppending, setIsAppending] = useState(false);

  const [hasMoreBefore, setHasMoreBefore] = useState(true);
  const [hasMoreAfter, setHasMoreAfter] = useState(true);

  const loadMoreBefore = useCallback(async () => {
    if (!virtuosoRef.current) return false;
    const messageList = getMessageList();
    if (messageList.length === 0) return;

    const response = await pagedMessageListAPI({
      variables: {
        input: {
          messageThreadId: messageThreadRef.current.id,
          pageSize: INITIAL_PAGE_SIZE,
          isAfter: false,
          messageCreateTime: messageList[0].createTime,
        },
      },
    });
    const _messageList = response.data?.pagedMessageList;
    setHasMoreBefore((_messageList?.length ?? 0) > 0);
    if (_messageList?.length) {
      virtuosoRef.current?.data.prepend(_messageList);
    }
  }, [getMessageList, pagedMessageListAPI, virtuosoRef]);

  const loadMoreAfter = useCallback(async () => {
    const messageList = getMessageList();
    if (messageList.length === 0) return;
    const response = await pagedMessageListAPI({
      variables: {
        input: {
          messageThreadId: messageThreadRef.current.id,
          pageSize: INITIAL_PAGE_SIZE,
          isAfter: true,
          messageCreateTime: messageList[messageList.length - 1].createTime,
        },
      },
    });
    const _messageList = response.data?.pagedMessageList;
    setHasMoreAfter((_messageList?.length ?? 0) > 0);
    if (_messageList?.length) {
      virtuosoRef.current?.data.append(_messageList);
    }
  }, [getMessageList, pagedMessageListAPI, virtuosoRef]);

  const lastListOffset = useRef(0);

  const debouncedSetDirection = useMemo(
    () =>
      debounce(
        (offset: number, lastOffset: number) => {
          if (offset < lastOffset) _setScrollDirection("down");
          else _setScrollDirection("up");
        },
        100,
        { leading: true, trailing: false, maxWait: 200 },
      ),
    [_setScrollDirection],
  );

  const onScroll = useCallback(
    async (location: ListScrollLocation) => {
      debouncedSetDirection(location.listOffset, lastListOffset.current);
      lastListOffset.current = location.listOffset;

      if (
        location.listOffset > AT_TOP_THRESHOLD &&
        !isPrependingRef.current &&
        hasMoreBefore
      ) {
        isPrependingRef.current = true;
        setIsPrepending(true);
        try {
          await loadMoreBefore();
        } catch {
          // a failed page fetch just means no new items; scrolling retries it
        } finally {
          isPrependingRef.current = false;
          setIsPrepending(false);
        }
        return;
      }
      if (location.isAtBottom && !isAppendingRef.current && hasMoreAfter) {
        isAppendingRef.current = true;
        setIsAppending(true);
        try {
          await loadMoreAfter();
        } catch {
          // a failed page fetch just means no new items; scrolling retries it
        } finally {
          isAppendingRef.current = false;
          setIsAppending(false);
        }
      }
    },
    [
      debouncedSetDirection,
      hasMoreBefore,
      hasMoreAfter,
      loadMoreBefore,
      loadMoreAfter,
    ],
  );

  const computeItemKey = useCallback(
    // data can transiently be missing (e.g. a page fetch that returned no
    // items); fall back to the index so one bad entry can't crash the render
    (data: VirtuosoMessageListData) => data.data?.id ?? `missing-${data.index}`,
    [],
  );

  const renderMessage: VirtuosoMessageListProps<
    IMessageFragmentWithIsDraft,
    VirtuosoMessageListContext
  >["ItemContent"] = useCallback(
    ({
      data,
      prevData,
      index,
    }: {
      data: IMessageFragmentWithIsDraft;
      prevData?: IMessageFragmentWithIsDraft | null;
      index: number;
    }) => {
      const showDateSeparator =
        index > 0 &&
        (!prevData ||
          !moment(data.createTime).isSame(prevData.createTime, "day"));

      const dateSeparatorDate = showDateSeparator ? data.createTime : undefined;

      return (
        <VirtuosoMessage
          message={data}
          messageThread={messageThreadRef.current}
          dateSeparatorDate={dateSeparatorDate}
          isUnseenMessage={data.id === unseenMessageId}
          showDateSeparator={showDateSeparator}
        />
      );
    },
    [unseenMessageId],
  );

  useEffect(
    () => () => {
      queueRef.current.clear(); //queueRef should drain on unmount
      debouncedSetDirection.cancel(); //debouncedSetDirection should cancel on unmount
    },
    [debouncedSetDirection],
  );

  const virtuosoMessageListContext = useMemo(
    (): VirtuosoMessageListContext => ({
      messageThreadId: messageThreadRef.current.id,
      isMeRemoved,
      isPrepending,
      isAppending,
      type: messageThreadRef.current.type,
      hasLastMessage,
      hasFirstMessage: !hasMoreBefore,
      initializeMessageList,
    }),
    [
      isMeRemoved,
      isPrepending,
      isAppending,
      hasLastMessage,
      hasMoreBefore,
      initializeMessageList,
    ],
  );

  return {
    virtuosoRef,
    virtuosoMessageListContext,
    onScroll,
    computeItemKey,
    renderMessage,
  };
};

export default usePagedMessageList;
